import React, { useEffect, useState } from "react";
import PageTitle from "../../../jsx/layouts/PageTitle";
import { enhancedHandoverIntentService, getErrorMessage } from "../../../services/enhancedAccreditationApiService";
import Pagination from "../permitUsers/Pagination";

const HandoverIntent = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 10;

  const [filters, setFilters] = useState({ handoverId: "" }); // handoverId holds ONLY the numeric/zero-padded part

  // No user type filter needed

  const formatHandoverId = (id) => {
    if (id === null || id === undefined) return "-";
    // If API already sends formatted string like HAIN00015, show as-is
    if (typeof id === 'string') return id;
    if (isNaN(Number(id))) return "-";
    const n = Number(id);
    return `HND${String(n).padStart(4, '0')}`;
  };

  const sanitizeDigits = (value) => String(value || '').replace(/\D/g, "");
  const buildHainId = (value) => {
    const digits = sanitizeDigits(value);
    if (!digits) return undefined;
    const padded = digits.padStart(5, '0');
    return `HND${padded}`;
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      setError("");
      const params = {
        page,
        limit: pageSize,
        handover_id: buildHainId(filters.handoverId),
      };
      const res = await enhancedHandoverIntentService.listHandovers(params);
      if (res.success) {
        setRows(res.data || []);
        setTotalPages(res.totalPages || 1);
        setTotalItems(res.totalItems || 0);
      } else {
        setRows([]);
      }
    } catch (e) {
      setError(getErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters.handoverId]);

  const handleDownload = async (pdfUrl, id) => {
    try {
      if (pdfUrl) {
        // Direct URL provided by API
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";
        link.rel = "noopener";
        link.download = `handover_${id || ''}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      // Fallback to API download by id
      const res = await enhancedHandoverIntentService.downloadPdf(id);
      const blob = res.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `handover_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <div className="card">
      <PageTitle motherMenu={"Handover"} activeMenu={"Handover Intent"} />
      <div className="card-header d-flex align-items-end justify-content-between border-bottom-0">
        <div className="d-flex gap-2 align-items-end">
          <div>
            <label className="form-label small text-muted mb-1">Handover ID</label>
            <div className="input-group input-group-sm" style={{ width: 220 }}>
              <span className="input-group-text">HND</span>
              <input
                className="form-control"
                value={filters.handoverId}
                placeholder="Numeric Value"
                onChange={(e) => setFilters((f) => ({ ...f, handoverId: sanitizeDigits(e.target.value) }))}
              />
            </div>
          </div>
          <button className="btn btn-outline-primary btn-sm" style={{ height: 50 }} onClick={() => { setFilters({ handoverId: "" }); setPage(1); }} disabled={loading}>
            Clear
          </button>
          <button className="btn btn-primary btn-sm" style={{ height: 50 }} disabled={loading} onClick={async () => {
            try {
              const res = await enhancedHandoverIntentService.exportLegacy({ handover_id: buildHainId(filters.handoverId) });
              const blob = res.data;
              const url = window.URL.createObjectURL(blob);
              const link = document.createElement("a");
              link.href = url;
              const hain = buildHainId(filters.handoverId);
              const filename = hain ? `legacy_handovers_${hain}.xlsx` : `legacy_handovers.xlsx`;
              link.setAttribute("download", filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              window.URL.revokeObjectURL(url);
            } catch (e) {
              setError(getErrorMessage(e));
            }
          }}>
            Export
          </button>
        </div>
        <div className="text-muted small">{totalItems} results</div>
      </div>
      <div className="card-body">
        {error && <div className="alert alert-danger">{error}</div>}
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th className="text-center">S No.</th>
                <th className="text-center">Handover ID</th>
                <th className="text-center">Receiver's Name</th>
                <th className="text-center">Receiver's Contact</th>
                <th className="text-center">Issued By</th>
                
                <th className="text-center">Date</th>
                <th className="text-center">Time</th>
                <th className="text-center">PDF</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">No records</td>
                </tr>
              ) : rows.map((r, idx) => (
                <tr key={idx}>
                  <td className="text-center">{(page - 1) * pageSize + idx + 1}</td>
                  <td className="text-center">{formatHandoverId(r?.handover_id ?? r?.id)}</td>
                  <td className="text-center">{r?.handover_person_name || r?.poc_name || "-"}</td>
                  <td className="text-center">{r?.handover_person_contact || r?.poc_contact || "-"}</td>
                  <td className="text-center">{r?.issued_by_name || "-"}</td>
                  
                  <td className="text-center">{r?.handover_datetime ? new Date(r.handover_datetime).toLocaleDateString() : (r?.date ? new Date(r.date).toLocaleDateString() : "-")}</td>
                  <td className="text-center">{r?.handover_datetime ? new Date(r.handover_datetime).toLocaleTimeString() : (r?.time || (r?.date ? new Date(r.date).toLocaleTimeString() : "-"))}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleDownload(r?.pdf, r?.handover_id || r?.id)} disabled={!r?.pdf && !r?.handover_id && !r?.id}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default HandoverIntent;


